import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import apiClient from '@/api/client';
import { useAuthStore } from '@/stores/authStore';

function ModuleBanner() {
  const [show, setShow] = useState(false);
  const [modules, setModules] = useState<{ id: string; name: string }[]>([]);
  const [admins, setAdmins] = useState<{ email: string; name: string; module: string }[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient
      .get('/auth/me')
      .then((r) => {
        // Update store first
        useAuthStore.getState().setUser({
          id: r.data.id,
          email: r.data.email,
          full_name: r.data.full_name,
          is_superuser: r.data.is_superuser,
          role: r.data.role,
        });
        // Super admins never see this banner
        if (r.data.is_superuser || r.data.role === 'super_admin') return;
        const mods = r.data.modules || [];
        if (mods.length === 0) {
          setAdmins(r.data.module_admins || []);
          apiClient
            .get('/modules')
            .then((mr) => {
              if (mr.data.length === 0) {
                // No modules exist at all — assign Generic silently
                apiClient.put('/auth/me/modules', { module_ids: ['generic'] }).catch(() => {});
              } else {
                setModules(mr.data);
                setShow(true);
              }
            })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      await apiClient.put('/auth/me/modules', { module_ids: Array.from(selected) });
      setShow(false);
      window.location.reload();
    } catch {
      /* fallback handled by contact info */
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;
  return (
    <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-3 space-y-2">
      <p className="text-xs text-amber-300 font-medium">Welcome! Select your module(s) to get started:</p>
      <div className="flex flex-wrap gap-2 items-center">
        {modules.map((m) => (
          <button
            key={m.id}
            onClick={() =>
              setSelected((prev) => {
                const next = new Set(prev);
                if (next.has(m.id)) next.delete(m.id);
                else next.add(m.id);
                return next;
              })
            }
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
              selected.has(m.id)
                ? 'bg-brand/20 border-brand/50 text-brand-light'
                : 'border-border text-text-muted hover:border-brand/30'
            }`}
          >
            {m.name}
          </button>
        ))}
        {selected.size > 0 && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="ml-2 text-xs px-4 py-1 rounded bg-brand text-white font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Confirm'}
          </button>
        )}
      </div>
      {admins.length > 0 && (
        <p className="text-[10px] text-text-muted">
          Need elevated access? Contact module admins:{' '}
          {admins.map((a, i) => (
            <span key={i}>
              <strong className="text-text-secondary">{a.name || a.email}</strong>
              <span className="text-text-muted"> ({a.module})</span>
              {i < admins.length - 1 ? ', ' : ''}
            </span>
          ))}
        </p>
      )}
      {admins.length === 0 && (
        <p className="text-[10px] text-text-muted">
          Module not listed? Contact <strong>kirankumar.n@saviynt.com</strong> or{' '}
          <strong>rakesh.kushwaha@saviynt.com</strong>
        </p>
      )}
    </div>
  );
}

/**
 * Root layout: fixed sidebar (left) + scrollable main area with topbar.
 */
import ToastContainer from '@/components/ui/ToastContainer';

export function AppShell() {
  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <Topbar />
        <ModuleBanner />
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
      <ToastContainer />
    </div>
  );
}
